import { useEffect } from 'react';

// Clamps a (possibly persisted) page number down to the available range once
// the underlying data has loaded. Without this, a stale out-of-range page
// restored from sessionStorage (after the result set shrank via deletions or a
// narrower filter) would strand the user on an empty page. Gated on `ready` so
// it never fires while data is still loading (totalPages defaults to 1 then,
// which would wrongly reset a valid persisted page before its query resolves).
const useClampPage = (
  page: number,
  totalPages: number,
  ready: boolean,
  setPage: (page: number) => void,
): void => {
  useEffect(() => {
    // Page numbers are 1-based, so a page < 1 is always invalid (page=0 fails the
    // server's page.min(1) validator and turns an empty list into a load error).
    // Floor it immediately, regardless of load state - this also rescues a stale
    // page=0 already stuck in sessionStorage, which the upper-bound clamp below
    // (page > totalPages) would never catch.
    if (page < 1) {
      setPage(1);
      return;
    }
    // Once data has loaded, pull an out-of-range page back down to the last page.
    if (ready && page > totalPages) setPage(Math.max(1, totalPages));
  }, [page, totalPages, ready, setPage]);
};

export default useClampPage;
